/* eslint-disable jsx-a11y/mouse-events-have-key-events */
/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, useEffect, useContext, useCallback } from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import { ViewModelForm, ViewModelServiceContext } from '@xengage/gw-portals-viewmodel-react';
import { useTranslator } from '@jutro/locale';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { CurrencyValue, useModal } from '@jutro/components';
import { UserService, MLAgentChoiceReportService } from 'gw-capability-gateway';
import { platformMessages, FormModalPopover } from 'ml-components-platform-react';
import { MLProductCodeUtil } from 'ml-portals-util-js';
import { MLPolicyTypeUtil, MLActivityDateUtil } from 'ml-portals-util-react';
import { MLBundleService, MLStateRolloutService } from 'ml-services-common-react';
import { MLModalService, MLLocaleService, MLTagManagerService } from 'ml-portals-services-react';
import appConfig from 'app-config';
import metadata from './MLAccountQuotesComponent.metadata.json5';
import messages from './MLAccountQuotesComponent.messages';
import styles from './MLAccountQuotesComponent.module.scss';

import mlBundleQuestionPopup from '../MLBundleQuestionPopup/MLBundleQuestionPopup';
import mlBundleQuestionPopupMsg from '../MLBundleQuestionPopup/MLBundleQuestionPopup.messages.js';

import { Link } from '@jutro/router';

const MAX_DESC_LENGTH_TO_DISPLAY = 14;
const INITIAL_MAX_QUOTES = 8;
const ALL_CODE = 'ALL';

const {
    MLUserTypes
} = appConfig;

const MLAccountQuotesComponent = (props) => {
    const modalApi = useModal();
    const {
        quotes,
        history
    } = props;

    const translator = useTranslator();
    const viewModelService = useContext(ViewModelServiceContext);
    const { authHeader } = useAuthentication();

    const {
        getCurrencySymbol,
        getDefaultCurrencySymbol
    } = new MLLocaleService()

    const {
        showReportModalWithQuoteID
    } = MLAgentChoiceReportService();

    const {
        showErrorModal,
        showProgressDialog
    } = MLModalService();

    const {
        getPolicyTypeName
    } = MLPolicyTypeUtil();

    const {
        getActivityDate,
        toLocaleTimeStamp
    } = MLActivityDateUtil();

    const AUTO_CODE = MLProductCodeUtil.getPersonalAutoCode();
    const HOME_CODE = MLProductCodeUtil.getHomeOwnersCode();
    const UMB_CODE = MLProductCodeUtil.getPersonalUmbrellaCode();

    const isBookrollDisplay = quotes.filter(
        (quote) => quote?.bookrollIndicator_ML?.search(/with/i) >= 0)
        ?.length > 0;
    const [filterCode, setFilterCode] = useState(ALL_CODE);
    const [maxQuotes, setMaxQuotes] = useState(INITIAL_MAX_QUOTES);
    const [isChoiceAgentUser, setIsChoiceAgentUser] = useState(false);
    const [isPcsOrIa, setIsPcsOrIa] = useState(false)
    const [filteredQuotes, setFilteredQuotes] = useState(quotes);

    useEffect(() => {
        UserService.getCurrentUser(authHeader).then((user) => {
            setIsChoiceAgentUser(user.isChoiceAgent_ML);
            setIsPcsOrIa(user.userType === MLUserTypes.PCS || user.userType === MLUserTypes.IA);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const filterQuotes = useCallback((quote) => {
        return filterCode === ALL_CODE || quote.product.publicID === filterCode;
    }, [filterCode]);

    useEffect(() => {
        if (!_.isEmpty(quotes)) {
            setFilteredQuotes(quotes.filter(filterQuotes));
        }
    }, [filterCode, quotes, filterQuotes])

    const getQuotesCountByCode = (code) => {
        return quotes.filter((quote) => quote.product.publicID === code).length;
    };

    const getQuotesCountDescription = (code) => {
        const count = getQuotesCountByCode(code);
        switch (count) {
            case 0: {
                return translator(messages.none);
            }
            case 1: {
                return translator(messages.oneQuote);
            }
            default: {
                return translator(messages.xQuotes, { number: getQuotesCountByCode(code) });
            }
        }
    }

    const setFilterActive = (code) => {
        setFilterCode(code);
    };

    const isAlaska7Day = (quote) => {
        return quote.address_ML.state === 'AK' && quote.termType_ML === '7Days';
    };

    const isBookrollQuote = (quote) => {
        return quote.bookrollIndicator_ML === 'With Rate Capping' || quote.bookrollIndicator_ML === 'Without Rate Capping';
    };

    const toggleMaxQuotes = () => {
        if (maxQuotes === INITIAL_MAX_QUOTES) {
            MLTagManagerService.sendLinkClickEvent(`${MLTagManagerService.getStepName()}_Quotes_Expand`);
            setMaxQuotes(quotes.length);
        } else {
            MLTagManagerService.sendLinkClickEvent(`${MLTagManagerService.getStepName()}_Quotes_Collapse`);
            setMaxQuotes(INITIAL_MAX_QUOTES);
        }
    };

    const getToggleButtonDisplayName = () => {
        if (maxQuotes === INITIAL_MAX_QUOTES) {
            return translator(messages.expand);
        }

        return translator(messages.collapse);
    };

    const canToggleQuotesLength = () => {
        return quotes.length > INITIAL_MAX_QUOTES;
    };

    const isChoiceAgent = (quote) => {
        return isChoiceAgentUser && quote.isChoiceAgentSubmission_ML;
    };

    const openAgentChoiceModal = (jobNumber) => {
        showReportModalWithQuoteID(jobNumber);
    };

    const resumeQuote = (quote) => {
        if (quote.isBundle_ML) {
            const bundleCheckPromise = MLBundleService.checkIsCRBridgedQuote(quote.jobNumber, authHeader)
                .then((isCRBridgeQuote) => {
                    if(isCRBridgeQuote && isCRBridgeQuote.length > 0) {
                        // Check state rollout eligible
                        return MLStateRolloutService.checkIsCRWizardEligible(quote.jobNumber, authHeader)
                            .then((isCREligible) => {
                                if(isCREligible) {
                                    const quoteEntryInfo = {
                                        account: quote.accountNumber,
                                        quoteID: quote.jobNumber
                                    };
                                    
                                    history.push('/quoteentry', quoteEntryInfo);
                                } else {
                                    if (isBookrollQuote(quote)) {
                                        MLBundleService.loadBundleCart(quote.accountNumber, history);
                                    } else {
                                        MLBundleService.updateBundleAndGotoAgentBundleCart(quote.accountNumber, quote.jobNumber, showProgressDialog, viewModelService, showErrorModal, history, authHeader);
                                    }
                                }
                            })
                    } else {
                        if (isBookrollQuote(quote)) {
                            MLBundleService.loadBundleCart(quote.accountNumber, history);
                        } else {
                            MLBundleService.updateBundleAndGotoAgentBundleCart(quote.accountNumber, quote.jobNumber, showProgressDialog, viewModelService, showErrorModal, history, authHeader);
                        }
                    }
                });
                
            showProgressDialog(platformMessages.loading, bundleCheckPromise);
        } else if (isBookrollQuote(quote) || isAlaska7Day(quote)) {
            if (quote.status === 'Quoted') {
                history.push('/quoteentry', {
                    quoteID: quote.jobNumber,
                    postalCode: quote.address_ML.postalCode,
                });
            } else {
                const modalTitle = messages.resumeQuotePopupTitle;
                let modalMsg = '';
                if (isBookrollQuote(quote)) {
                    modalMsg = messages.resumeQuotePopupMsgBookroll;
                } else if (isPcsOrIa) {
                    modalMsg = messages.resumeQuotePopupMsgPcsIa;
                } else {
                    modalMsg = messages.resumeQuotePopupMsgDefault;
                }
                showErrorModal(modalTitle, modalMsg, messages.close);
            }
        } else {
            const componentProps = {
                title: mlBundleQuestionPopupMsg.savedQuote,
                overrideProps: {
                    accountNumber: quote.accountNumber,
                    quoteNumber: quote.jobNumber,
                    postalCode: quote.address_ML.postalCode,
                    viewModelService,
                    history,
                    authHeader
                },
                CustomComponent: mlBundleQuestionPopup,
                showFooterOnCustomComponent: false,
                additionalPEStyle: 'customFooterModal'
            };

            modalApi.showModal(
                <FormModalPopover {...componentProps} />
            );
        }
    };

    const getTotalPremiumCurrencySymbol = (totalPremium) => {
        if (totalPremium && totalPremium.currency) {
            return getCurrencySymbol(totalPremium.currency);
        }
        return getDefaultCurrencySymbol();
    };

    const descriptionToShow = (description) => {
        if (!_.isNil(description) && description.length > MAX_DESC_LENGTH_TO_DISPLAY) {
            return `${description.slice(0, MAX_DESC_LENGTH_TO_DISPLAY)}...`;
        }
        return description;
    };

    const showFull = (event, description) => {
        event.target.innerHTML = description;
    };

    const showTruncate = (event, description) => {
        event.target.innerHTML = descriptionToShow(description);
    };

    const getQuotesTableOverrides = () => {
        const getEditTime = (quote) => {
            return toLocaleTimeStamp(quote.editTime_ML)
        };

        const getJobNumber = (quote) => {
            return <Link mlTagManagerLinkClick="Account Summary_Quotes_Saved Quote" onClick={() => resumeQuote(quote)}>{quote.jobNumber}</Link>
        };

        const getBundle = (quote) => {
            return translator(quote.bundleID_ML > 0 ? platformMessages.mlYes : platformMessages.mlNo);
        };

        const getPolicyType = (quote) => {
            const { product } = quote;
            if (MLProductCodeUtil.isHomeOwners(product.publicID)) {
                return getPolicyTypeName(product.coveragePartType_ML);
            }
            return getPolicyTypeName(product.publicID);
        };

        const getBookrollIndicator = (quote) => {
            return quote.bookrollIndicator_ML;
        };

        const getInitiator = (quote) => {
            if (isChoiceAgent(quote)) {
                return <Link onClick={() => openAgentChoiceModal(quote.jobNumber)}>{translator(messages.choiceAgent)}</Link>
            }
            return _.get(quote, 'initiator_ML', '-');
        };

        const getTotalPremium = (quote) => {
            if (quote.status === 'Quoted') {
                return (
                    <CurrencyValue
                        currency={_.get(quote, 'totalPremium.currency')}
                        value={_.get(quote, 'totalPremium.amount')}
                    />
                );
            }
            return `${getTotalPremiumCurrencySymbol(quote.totalPremium)}-`
        };

        const getDescription = (quote) => {
            return (
                <div
                    onMouseOver={(event) => showFull(event, quote.nickname_ML || '-')}
                    onMouseLeave={(event) => showTruncate(event, quote.nickname_ML || '-')}
                >
                    {descriptionToShow(quote.nickname_ML) || '-'}
                </div>
            );
        };

        const overrides = {
            quotesTable: {
                data: _.take(_.orderBy(filteredQuotes, ['bundleID_ML', 'editTime_ML'], ['asc', 'desc']), maxQuotes)
            },
            editTimeColumn: {
                renderCell: getEditTime
            },
            jobNumberColumn: {
                renderCell: getJobNumber
            },
            bundleColumn: {
                renderCell: getBundle
            },
            policyTypeColumn: {
                renderCell: getPolicyType
            },
            bookrollIndicatorColumn: {
                visible: isPcsOrIa && isBookrollDisplay,
                renderCell: getBookrollIndicator
            },
            initiatorColumn: {
                renderCell: getInitiator
            },
            totalPremiumColumn: {
                renderCell: getTotalPremium
            },
            descriptionColumn: {
                renderCell: getDescription
            }
        };

        return overrides;
    };

    const overrideProps = {
        ...getQuotesTableOverrides(),
        allQuotesFilter: {
            onClick: () => setFilterActive(ALL_CODE)
        },
        autoQuotesFilter: {
            onClick: () => setFilterActive(AUTO_CODE)
        },
        autoQuotesCount: {
            content: getQuotesCountDescription(AUTO_CODE)
        },
        homeQuotesFilter: {
            onClick: () => setFilterActive(HOME_CODE)
        },
        homeQuotesCount: {
            content: getQuotesCountDescription(HOME_CODE)
        },
        umbrellaQuotesFilter: {
            onClick: () => setFilterActive(UMB_CODE)
        },
        umbrellaQuotesCount: {
            content: getQuotesCountDescription(UMB_CODE)
        },
        toggleMaxQuotesButton: {
            visible: canToggleQuotesLength(),
            content: getToggleButtonDisplayName(),
            onClick: toggleMaxQuotes
        }
    };

    const resolvers = {
        resolveClassNameMap: styles
    };

    return (
        <ViewModelForm
            uiProps={metadata.componentContent}
            overrideProps={overrideProps}
            classNameMap={resolvers.resolveClassNameMap}
        />
    )
};

MLAccountQuotesComponent.propTypes = {
    quotes: PropTypes.arrayOf(PropTypes.shape({})),
    history: PropTypes.shape({ push: PropTypes.func }).isRequired,
};

MLAccountQuotesComponent.defaultProps = {
    quotes: []
};

export default withRouter(MLAccountQuotesComponent);